import { useState, useEffect } from 'react';
import i18n from '@/i18n';

type Currency = 'BRL' | 'USD';

async function tryFetch(url: string, extract: (data: any) => string | null): Promise<string | null> {
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const data = await res.json();
    return extract(data);
  } catch {
    return null;
  }
}

async function detectCountry(): Promise<string | null> {
  return (
    (await tryFetch('https://api.country.is/', d => d?.country)) ??
    (await tryFetch('https://freeipapi.com/api/json', d => d?.countryCode)) ??
    (await tryFetch('https://ipapi.co/json/', d => d?.country_code)) ??
    null
  );
}

export function useGeolocatedCurrency() {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    detectCountry()
      .then(countryCode => {
        const isBrazil = countryCode === 'BR';
        const detected: Currency = isBrazil ? 'BRL' : 'USD';
        setCurrency(detected);
        console.log('[geo] Detected country:', countryCode, '→ currency:', detected);

        // Auto-set language if no explicit preference saved
        if (isBrazil && !localStorage.getItem('app-language')) {
          i18n.changeLanguage('pt-BR');
          localStorage.setItem('app-language', 'pt-BR');
        }
      })
      .catch(() => {
        console.warn('[geo] All IP APIs failed, defaulting to USD');
        setCurrency('USD');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const formatPrice = (usdCents: number) => {
    if (currency === 'BRL') {
      const brlValue = (usdCents / 100) * 5.5;
      return `R$ ${Math.round(brlValue).toLocaleString('pt-BR')}`;
    }
    return `$${(usdCents / 100).toLocaleString('en-US')}`;
  };

  const currencySymbol = currency === 'BRL' ? 'R$' : '$';

  return { currency, isLoading, formatPrice, currencySymbol };
}
